import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Building2, Download, LogOut, Users, TrendingUp, Mail, Phone, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [buyerProfiles, setBuyerProfiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterRolle, setFilterRolle] = useState('all');
  const [filterQuelle, setFilterQuelle] = useState('all');
  const [activeTab, setActiveTab] = useState('leads'); // 'leads' or 'profiles'
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate, filterRolle, filterQuelle]);

  const fetchData = async () => {
    const token = localStorage.getItem('admin_token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      setLoading(true);
      
      // Fetch stats
      const statsResponse = await axios.get(`${API}/admin/stats`, { headers });
      setStats(statsResponse.data);

      // Fetch leads with filters
      const params = {};
      if (filterRolle !== 'all') params.rolle = filterRolle;
      if (filterQuelle !== 'all') params.quelle = filterQuelle;
      
      const leadsResponse = await axios.get(`${API}/admin/leads`, { headers, params });
      setLeads(leadsResponse.data);

      // Fetch buyer profiles
      try {
        const profilesResponse = await axios.get(`${API}/admin/buyer-profiles`, { headers });
        setBuyerProfiles(profilesResponse.data || []);
      } catch (err) {
        console.log('Buyer profiles endpoint not available yet');
        setBuyerProfiles([]);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        toast.error('Session abgelaufen. Bitte erneut anmelden.');
        handleLogout();
      } else {
        toast.error('Fehler beim Laden der Daten');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  const handleExportCSV = () => {
    if (leads.length === 0) {
      toast.error('Keine Leads zum Exportieren');
      return;
    }

    // Create CSV
    const headers = ['ID', 'Rolle', 'Name', 'Email', 'Telefon', 'Quelle', 'Erstellt am', 'Consent Kontakt', 'Consent Marketing'];
    const rows = leads.map(lead => [
      lead.id,
      lead.rolle,
      lead.name,
      lead.email,
      lead.telefon || '-',
      lead.quelle,
      new Date(lead.created_at).toLocaleString('de-DE'),
      lead.consent_contact ? 'Ja' : 'Nein',
      lead.consent_marketing ? 'Ja' : 'Nein'
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `retail-invest-leads-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('CSV exportiert!');
  };

  const adminEmail = localStorage.getItem('admin_email');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                style={{ background: 'linear-gradient(135deg, #D4A853 0%, #F0C870 100%)' }}
              >
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <div className="text-xl font-bold" style={{ color: 'var(--navy-900)' }}>
                  RetailInvest Admin
                </div>
                <div className="text-xs" style={{ color: 'var(--gray-500)' }}>
                  {adminEmail}
                </div>
              </div>
            </div>

            {/* Logout */}
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Abmelden
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="glass-card p-6 border-t-4" style={{ borderTopColor: 'var(--gold-600)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>Gesamt Leads</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: 'var(--navy-800)' }}>
                    {stats.leads.total}
                  </p>
                </div>
                <Users className="h-8 w-8" style={{ color: 'var(--gold-600)' }} />
              </div>
            </Card>

            <Card className="glass-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>Eigentümer</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: 'var(--navy-800)' }}>
                    {stats.leads.eigentuemer}
                  </p>
                </div>
                <Building2 className="h-8 w-8" style={{ color: 'var(--navy-600)' }} />
              </div>
            </Card>

            <Card className="glass-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>Investoren</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: 'var(--navy-800)' }}>
                    {stats.leads.investoren}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8" style={{ color: 'var(--navy-600)' }} />
              </div>
            </Card>

            <Card className="glass-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>WAULT Berechnungen</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: 'var(--navy-800)' }}>
                    {stats.tool_runs.wault}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8" style={{ color: 'var(--gold-600)' }} />
              </div>
            </Card>
          </div>
        )}

        {/* Tabs: Leads vs Buyer Profiles */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-2 gap-2">
            <TabsTrigger value="leads" className="px-6 py-3">
              <Users className="h-4 w-4 mr-2" />
              Leads ({leads.length})
            </TabsTrigger>
            <TabsTrigger value="profiles" className="px-6 py-3">
              <Search className="h-4 w-4 mr-2" />
              Käufer-Suchprofile ({buyerProfiles.length})
            </TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads" className="mt-6">
            {/* Filters & Export */}
            <Card className="glass-card p-6 mb-6">
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--gray-700)' }}>
                    Rolle filtern
                  </label>
                  <Select value={filterRolle} onValueChange={setFilterRolle}>
                    <SelectTrigger data-testid="filter-rolle">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Rollen</SelectItem>
                      <SelectItem value="Eigentümer">Eigentümer</SelectItem>
                      <SelectItem value="Investor">Investor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--gray-700)' }}>
                    Quelle filtern
                  </label>
                  <Select value={filterQuelle} onValueChange={setFilterQuelle}>
                    <SelectTrigger data-testid="filter-quelle">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Quellen</SelectItem>
                      <SelectItem value="Kontaktformular">Kontaktformular</SelectItem>
                      <SelectItem value="WAULT Tool">WAULT Tool</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={handleExportCSV}
                  data-testid="export-csv-button"
                  className="btn-gold-gradient text-white border-0 font-semibold"
                  disabled={leads.length === 0}
                >
                  <Download className="h-4 w-4 mr-2" />
                  CSV Exportieren
                </Button>
              </div>
            </Card>

        {/* Leads Table */}
        <Card className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-12 text-center">
                <p style={{ color: 'var(--gray-500)' }}>Lade Daten...</p>
              </div>
            ) : leads.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--gray-400)' }} />
                <p className="text-lg font-semibold mb-2" style={{ color: 'var(--gray-700)' }}>
                  Keine Leads gefunden
                </p>
                <p className="text-sm" style={{ color: 'var(--gray-500)' }}>
                  Leads erscheinen hier, sobald sie über die Website eingehen.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rolle</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Kontakt</TableHead>
                    <TableHead>Quelle</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead>Consent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                      <TableCell>
                        <span
                          className="inline-block px-3 py-1 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: lead.rolle === 'Eigentümer' ? 'var(--navy-100)' : 'var(--gold-100)',
                            color: lead.rolle === 'Eigentümer' ? 'var(--navy-700)' : 'var(--gold-700)'
                          }}
                        >
                          {lead.rolle}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">{lead.name}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-3 w-3" style={{ color: 'var(--gray-500)' }} />
                            <a href={`mailto:${lead.email}`} className="hover:underline" style={{ color: 'var(--navy-600)' }}>
                              {lead.email}
                            </a>
                          </div>
                          {lead.telefon && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-3 w-3" style={{ color: 'var(--gray-500)' }} />
                              <span style={{ color: 'var(--gray-600)' }}>{lead.telefon}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm" style={{ color: 'var(--gray-700)' }}>
                          {lead.quelle}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm" style={{ color: 'var(--gray-600)' }}>
                          {new Date(lead.created_at).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {lead.consent_contact && (
                            <div className="text-xs" style={{ color: 'var(--success-600)' }}>
                              ✓ Kontakt
                            </div>
                          )}
                          {lead.consent_marketing && (
                            <div className="text-xs" style={{ color: 'var(--success-600)' }}>
                              ✓ Marketing
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </Card>
          </TabsContent>

          {/* Buyer Profiles Tab */}
          <TabsContent value="profiles" className="mt-6">
            <Card className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="p-12 text-center">
                    <p style={{ color: 'var(--gray-500)' }}>Lade Suchprofile...</p>
                  </div>
                ) : buyerProfiles.length === 0 ? (
                  <div className="p-12 text-center">
                    <Search className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--gray-400)' }} />
                    <p className="text-lg font-semibold mb-2" style={{ color: 'var(--gray-700)' }}>
                      Keine Käufer-Suchprofile
                    </p>
                    <p className="text-sm" style={{ color: 'var(--gray-500)' }}>
                      Käufer-Suchprofile erscheinen hier, sobald Investoren ihre Kriterien hinterlegen.
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>E-Mail</TableHead>
                        <TableHead>Assetklassen</TableHead>
                        <TableHead>PLZ-Gebiete</TableHead>
                        <TableHead>Budget</TableHead>
                        <TableHead>WAULT Min</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Erstellt</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {buyerProfiles.map((profile) => (
                        <TableRow key={profile.id} data-testid={`profile-row-${profile.id}`}>
                          <TableCell className="font-medium">
                            <a href={`mailto:${profile.email}`} className="hover:underline" style={{ color: 'var(--navy-600)' }}>
                              {profile.email}
                            </a>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {profile.assetklassen.slice(0, 2).map((ak, idx) => (
                                <span key={idx} className="px-2 py-1 rounded text-xs" style={{ backgroundColor: 'var(--gold-100)', color: 'var(--gold-700)' }}>
                                  {ak}
                                </span>
                              ))}
                              {profile.assetklassen.length > 2 && (
                                <span className="text-xs" style={{ color: 'var(--gray-500)' }}>
                                  +{profile.assetklassen.length - 2}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm" style={{ color: 'var(--gray-700)' }}>
                              {profile.plz_gebiete.slice(0, 3).join(', ')}
                              {profile.plz_gebiete.length > 3 && '...'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm font-medium" style={{ color: 'var(--gray-700)' }}>
                              €{(profile.budget_min / 1000000).toFixed(1)}M - €{(profile.budget_max / 1000000).toFixed(1)}M
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm" style={{ color: 'var(--gray-600)' }}>
                              {profile.wault_min ? `${profile.wault_min}+ Jahre` : '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span
                              className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                                profile.is_active
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {profile.is_active ? 'Aktiv' : 'Inaktiv'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm" style={{ color: 'var(--gray-600)' }}>
                              {new Date(profile.created_at).toLocaleDateString('de-DE')}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;